import React, {createContext, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {CollectionType, ViewMode} from '@taopix/taopix-core';
import {useExperience} from '../../Experience/ExperienceProvider';
import useViewMode from '../../../Hooks/useViewMode';
import {LayoutSelector} from '../../../Selectors';
import {useUiState} from '../UserInterface/UserInterfaceContext';

export type PageActionButton = {
  name: string;
  order: number;
};

const initialState: PageActionButton[] = [
  {name: 'addPage', order: 0},
  {name: 'deletePage', order: 1},
  {name: 'duplicatePage', order: 2},
  {name: 'layout', order: 3},
  {name: 'addPictureBox', order: 4},
  {name: 'addTextBox', order: 5},
  {name: 'editPage', order: 6},
];

export const PageActionsContext = createContext<PageActionButton[]>([] as PageActionButton[]);

export const PageActionsProvider = (props: {children: any}) => {
  const {experience} = useExperience();
  const ui = useUiState();
  const [viewMode] = useViewMode();
  const layout = useSelector(LayoutSelector);
  const [state, setState] = useState<PageActionButton[]>(initialState);

  function reorderArray(arr: PageActionButton[]) {
    const sortedArray = arr.sort((a, b) => a.order - b.order);

    sortedArray.forEach((item, index) => {
      item.order = index + 1;
    });

    return sortedArray;
  }

  const viewModes = [
    experience.EDITOR.workArea.editingViewModes.list,
    experience.EDITOR.workArea.editingViewModes.grid,
    experience.EDITOR.workArea.editingViewModes.page,
  ];

  const permissions = useMemo(() => {
    const fixedPageCount = layout.minpages === layout.maxpages;

    switch (viewMode) {
      case ViewMode.Grid: {
        return {
          addPage:
            ui.state.isSmallScreen &&
            experience.EDITOR.workArea.gridView.addDeletePage &&
            !fixedPageCount, // The add pages button is only shown for grid view in small screen (i.e. in the page options palette)
          deletePage: experience.EDITOR.workArea.gridView.addDeletePage && !fixedPageCount,
          duplicatePage: experience.EDITOR.workArea.gridView.duplicatePage,
          layout: false, // no experience option
          addPictureBox: false, // no experience option
          addTextBox: false, // no experience optinon
          editPage: viewModes.filter(Boolean).length > 1,
        };
      }
      case ViewMode.Single: {
        return {
          addPage: experience.EDITOR.workArea.singlePage.addPage && !fixedPageCount,
          deletePage: experience.EDITOR.workArea.singlePage.deletePage && !fixedPageCount,
          duplicatePage: experience.EDITOR.workArea.singlePage.duplicatePage,
          layout: experience.EDITOR.workArea.singlePage.layout,
          addPictureBox: experience.EDITOR.workArea.singlePage.addPictureBox,
          addTextBox: experience.EDITOR.workArea.singlePage.addTextBox,
          editPage: false,
        };
      }
      case ViewMode.List: {
        return {
          addPage: experience.EDITOR.workArea.listView.addDeletePage && !fixedPageCount,
          deletePage: experience.EDITOR.workArea.listView.addDeletePage && !fixedPageCount,
          duplicatePage: experience.EDITOR.workArea.listView.duplicatePage,
          layout: experience.EDITOR.workArea.listView.layout,
          addPictureBox: experience.EDITOR.workArea.listView.addPictureBox,
          addTextBox: experience.EDITOR.workArea.listView.addTextBox,
          editPage: false,
        };
      }
      default: {
        return {
          addPage: !fixedPageCount,
          deletePage: !fixedPageCount,
          layout: true,
          addPictureBox: true,
          addTextBox: true,
          editPage: false,
          duplicatePage: false,
        };
      }
    }
  }, [viewMode, ui.state.isSmallScreen, layout, ui.state.canEditPageLayout]);

  useEffect(() => {
    // get a manifest of all active permissions
    const activeButtons = Object.keys(permissions).reduce<string[]>((acc, current) => {
      if (permissions[current]) {
        acc.push(current);
      }
      return acc;
    }, []);

    const c = activeButtons
      .map((curr, index) => {
        return {
          name: curr,
          order: initialState.find(entry => entry.name)?.order || index,
        };
      })
      .filter(entry => entry);

    const p = reorderArray(c);
    const a: PageActionButton[] = [];
    if (ui.state.isSmallScreen && p.length > (ViewMode.Grid === viewMode ? 0 : 3)) {
      const front = p.slice(0, ViewMode.Grid === viewMode ? 0 : 2);
      const remainder = p.slice(front.length, p.length);

      if (0 !== remainder.length) {
        a.push(...front);
      }

      a.push({
        name: 'showMore',
        order: front.length + 1,
      });

      remainder.forEach((item, index) => {
        item.order = index + (a.length + 1);
      });

      a.push(...remainder);

      setState(a);
    } else {
      setState(p);
    }
  }, [permissions, ui.state.isSmallScreen, ui.state.canEditPageLayout, viewMode]);

  return <PageActionsContext.Provider value={state}>{props.children}</PageActionsContext.Provider>;
};

export const usePageActions = () => {
  const context = useContext(PageActionsContext);
  if (context === undefined) {
    throw new Error('usePageActions must be used within a PageActionsProvider');
  }
  return context;
};
